import React, { FC, useMemo } from 'react';
import { usePostComposer } from 'app/modules/posts/modules/post/components/composer/PostComposerProvider';
import PageTagsSelect from 'components/shared/PageTagsSelect';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import { Icon, IconButton, Tag } from '@kontentino/ui';
import AccordionWidgetIcon from 'app/modules/posts/components/accordionWidget/AccordionWidgetIcon';
import IntercomUtils from 'app/utils/intercom';
import AccordionWidget from 'app/modules/posts/components/AccordionWidget';
import Link from 'components/shared/Link';
import { stopPropagation } from 'utils/mouseEvent';
import AccordionWidgetTitle from 'app/modules/posts/components/accordionWidget/AccordionWidgetTitle';
import { faCog } from '@fortawesome/pro-regular-svg-icons/faCog';
import LabelTag from 'components/shared/LabelTag';
import { profileSettingsLink } from 'app/config/settingsV2Mode';
import useSettingsV2Gate from 'app/hooks/useSettingsV2Gate';

type Props = {
  accordionProps?: Partial<React.ComponentProps<typeof AccordionWidget>>;
};

const Labels: FC<Props> = ({ accordionProps }) => {
  const { form, setForm } = usePostComposer();
  const { isSettingsV2Enabled } = useSettingsV2Gate();
  const visibleTags = form.tags.slice(0, 2);
  const truncatedTagsCount = form.tags.length - visibleTags.length;

  return useMemo(() => {
    return (
      <UserPermissionGate
        scopes={[
          UserPermission.TAG_CREATE,
          UserPermission.TAG_EDIT,
          UserPermission.TAG_LIST,
        ]}
        exact={false}
      >
        <AccordionWidget
          title={
            <AccordionWidgetTitle variant="labels" className="tw-justify-end" />
          }
          titleSuffix={(isOpen) =>
            !!visibleTags.length &&
            !isOpen && (
              <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
                {visibleTags.map((tag) => (
                  <LabelTag
                    key={tag.id}
                    size="small"
                    className="tw-max-w-[100px]"
                    name={tag.name}
                    color={tag.color}
                  />
                ))}
                {truncatedTagsCount > 0 && (
                  <Tag size="small" className="tw-whitespace-nowrap">
                    + {truncatedTagsCount}
                  </Tag>
                )}
              </div>
            )
          }
          icon={<AccordionWidgetIcon variant="labels" />}
          data-name="posts-composer_labels"
          {...accordionProps}
        >
          <div
            className="tw-flex tw-w-full tw-space-x-3"
            data-name="posts-composer_labels_input"
          >
            <div className="tw-min-w-0 tw-flex-[1]">
              <PageTagsSelect
                onChange={(updater) =>
                  setForm(({ tags }) => ({ tags: updater(tags) }))
                }
                value={form.tags}
                pageId={form.page.id}
                onFocus={() =>
                  IntercomUtils.track(
                    IntercomUtils.events.POST_LABELS_SELECT_FOCUSED,
                  )
                }
              />
            </div>

            {form.page.id && (
              <UserPermissionGate scopes={UserPermission.TAG_EDIT}>
                <Link
                  to={profileSettingsLink(
                    form.page.id,
                    'labels',
                    isSettingsV2Enabled,
                  )}
                  onClick={stopPropagation()}
                  target="_blank"
                >
                  <IconButton
                    variant="secondary"
                    icon={<Icon icon={faCog} />}
                  />
                </Link>
              </UserPermissionGate>
            )}
          </div>
        </AccordionWidget>
      </UserPermissionGate>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.tags, accordionProps, isSettingsV2Enabled]);
};

export default Labels;
